import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import ConditionalRenderer from '../ConditionalRenderer';
import ConditionalWrapper from '../ConditionalWrapper';
import Text, { TextProps } from './Text';
import { useCopy } from '@/hooks/shared/useCopy';
import { Tooltip } from './Tooltip';

type TextWithCopyProps = Omit<TextProps, 'text' | 'className' | 'target'> & {
  copyText?: string;
  copiedText?: string;
  target?: string;
  label: string;
  clickText?: boolean;
  className?: Pick<ClassNameComponent, 'base' | 'text' | 'span'>;
  truncate?: boolean;
};

export default function TextWithCopy({
  className,
  copyText,
  copiedText,
  clickText,
  label,
  target = label,
  truncate,
  ...textProps
}: TextWithCopyProps) {
  const { isCopied, handleCopy } = useCopy();
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const onClickButton: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    handleCopy(target);
  };

  return (
    <ConditionalRenderer condition={!!label}>
      <div className={twMerge('flex items-center gap-1', className?.base)}>
        <ConditionalWrapper
          condition={!!truncate}
          wrapper={
            <span className={twMerge('overflow-hidden', className?.span)} />
          }
        >
          <Text
            text={label}
            {...textProps}
            className={className?.text}
            onClick={() => clickText && handleCopy(target)}
          />
        </ConditionalWrapper>
        <Tooltip
          text={
            !isCopied
              ? copyText || tCommon('copy')
              : copiedText || tCommon('copied')
          }
          placement="right"
        >
          <IconButton
            icon={
              isCopied ? (
                <CheckCircleIcon
                  data-testid="checkIcon"
                  className="w-4 h-4 text-success"
                />
              ) : (
                <DocumentDuplicateIcon
                  data-testid="documentIcon"
                  className="w-4 h-4  text-primary"
                />
              )
            }
            onClick={onClickButton}
            className="opacity-75 hover:opacity-100 border-none transition ease-in-out duration-150"
          />
        </Tooltip>
      </div>
    </ConditionalRenderer>
  );
}
